/**
 * useLanguageSync — syncs i18n language across tabs via localStorage.
 *
 * Flow: init → useSyncSettings reads i18nextLng from localStorage.
 * changeLanguage → useSyncSettings setter writes to localStorage
 * → custom storage event fires → i18n.languageChanged listener in i18n.ts
 * updates documentElement.dir/lang.
 */
import { useEffect } from "react";
import { useSyncSettings } from "./useSyncSettings";
import i18n from "@/i18n";

const LNG_KEY = "i18nextLng";

export function useLanguageSync() {
  const [lng, setLng] = useSyncSettings(LNG_KEY, "en");

  // Sync i18n instance with stored preference on mount.
  // LanguageDetector has already set the language from localStorage by this point,
  // so this only applies when no language was detected yet (i.e., first visit).
  useEffect(() => {
    if (!i18n.language || i18n.language === "error") {
      i18n.changeLanguage(lng).then(() => {
        localStorage.setItem(LNG_KEY, lng);
      });
    }
  }, [lng]);

  const changeLanguage = (newLng: string) => {
    setLng(newLng);
  };

  return { lng, changeLanguage } as const;
}